import { useEffect, useRef, useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import useAuthStore from '@/stores/auth';
import CustomerOnboardingPage from '@/pages/CustomerOnboardingPage';
import Spinner from '@/components/ui/Spinner';

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export default function ProtectedRoute() {
  const { isAuthenticated, isLoading, initialize, clearAuth, tenant } = useAuthStore();
  const location = useLocation();
  const timerRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      clearAuth();
    }, IDLE_TIMEOUT_MS);
  }, [clearAuth]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Set up idle timeout listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handleActivity = () => resetTimer();

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the timer
    resetTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, resetTimer]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const activation = tenant?.customer_activation;
  const trialActive = activation?.state === 'awaiting_agreements' && activation?.trial_started_at && new Date(activation.trial_expires_at).getTime() > Date.now();
  if (activation && activation.state !== 'live') {
    if (!trialActive || location.pathname === '/onboarding') return <CustomerOnboardingPage />;
    return <Outlet />;
  }

  return <Outlet />;
}
